'use client';

import { useEffect, useRef } from 'react';
import type { Locale } from '../HomeLanding';

const baseLogistics = [
  { label: 'Shelf Life', value: '12 months from production date' },
  { label: 'Storage', value: 'Cool, dry place, shielded from direct sunlight in airtight tanks or drums' },
  { label: 'Packaging', value: '190 Kg Steel/Plastic Drums, 1000 Kg IBC Tanks, or Flexibags' },
  { label: 'Certifications', value: 'COA, Health Certificate, MSDS, Fumigation, and Certificate of Origin (COO)' },
  { label: 'Delivery Terms', value: 'FOB / CIF / CNF (Lead time: 30-60 days)' },
  { label: 'Payment Terms', value: 'T/T, 100% Irrevocable L/C at Sight, or 100% Advance' },
];

export default function LogisticsCompliance({ locale = 'en' }: { locale?: Locale }) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isIndonesian = locale === 'id';
  const isVietnamese = locale === 'vn';
  const isChinese = locale === 'cn';

  const logistics =
    locale === 'vn'
      ? [
          { label: 'Hạn sử dụng', value: '12 tháng kể từ ngày sản xuất' },
          { label: 'Bảo quản', value: 'Nơi mát, khô, tránh ánh nắng trực tiếp trong bồn hoặc phuy kín' },
          { label: 'Đóng gói', value: 'Phuy thép/nhựa 190 Kg, bồn IBC 1000 Kg hoặc flexibag' },
          { label: 'Chứng từ', value: 'COA, Health Certificate, MSDS, Fumigation và Certificate of Origin (COO)' },
          { label: 'Điều kiện giao hàng', value: 'FOB / CIF / CNF (thời gian giao: 30-60 ngày)' },
          { label: 'Điều khoản thanh toán', value: 'T/T, L/C không hủy ngang trả ngay 100%, hoặc trả trước 100%' },
        ]
      : locale === 'id'
        ? [
            { label: 'Masa simpan', value: '12 bulan sejak tanggal produksi' },
            { label: 'Penyimpanan', value: 'Simpan di tempat sejuk, kering, terlindung dari sinar matahari langsung di tangki atau drum kedap udara' },
            { label: 'Kemasan', value: 'Drum baja/plastik 190 Kg, tangki IBC 1000 Kg, atau flexibag' },
            { label: 'Sertifikasi', value: 'COA, Health Certificate, MSDS, Fumigation, dan Certificate of Origin (COO)' },
            { label: 'Syarat pengiriman', value: 'FOB / CIF / CNF (lead time: 30-60 hari)' },
            { label: 'Syarat pembayaran', value: 'T/T, 100% Irrevocable L/C at Sight, atau 100% Advance' },
          ]
      : locale === 'cn'
          ? [
              { label: '保质期', value: '自生产日起 12 个月' },
              { label: '储存', value: '置于阴凉干燥处，避免阳光直射，密封储罐或桶装保存' },
              { label: '包装', value: '190 Kg 钢桶/塑料桶、1000 Kg IBC 罐或柔性袋' },
              { label: '证书', value: 'COA、健康证书、MSDS、熏蒸证明及原产地证书（COO）' },
              { label: '交货条款', value: 'FOB / CIF / CNF（交期：30-60 天）' },
              { label: '付款条款', value: 'T/T、100% 不可撤销即期 L/C 或 100% 预付' },
            ]
          : baseLogistics;

  useEffect(() => {
    if (!sectionRef.current) return;
    const items = sectionRef.current.querySelectorAll('.lc-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Array.from(items).indexOf(entry.target as Element);
            setTimeout(() => entry.target.classList.add('active'), index * 70);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="compliance" ref={sectionRef} className="py-24 bg-canvas scroll-mt-20">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="lc-reveal reveal mb-8 flex items-center gap-3">
          <span className="inline-block w-6 h-px bg-blue" />
          <span className="font-mono text-[11px] tracking-[0.18em] text-blue uppercase font-medium">
            {isVietnamese
              ? 'Logistics & tuân thủ'
              : isIndonesian
                ? 'Logistik & kepatuhan'
                : isChinese
                  ? '物流与合规'
                  : 'Logistics & Compliance'}
          </span>
        </div>

        <div className="lc-reveal reveal reveal-delay-1 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <h2
              className="font-bold tracking-[-0.04em] text-charcoal leading-tight"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              {isVietnamese
                ? 'Nguồn cung sẵn sàng giao dịch.'
                : isIndonesian
                  ? 'Suplai Siap Ekspor.'
                  : isChinese
                    ? '可出口供应。'
                    : 'Trade-ready supply.'}
              <br />
              <span className="text-titanium font-light">
                {isVietnamese
                  ? 'Đóng gói cho xuất khẩu, chứng từ sẵn sàng kiểm tra.'
                  : isIndonesian
                    ? 'Standar Pengemasan Internasional & Dokumen Lengkap.'
                    : isChinese
                      ? '适合出口的包装与审核文件。'
                      : 'Packed for export, documented for audit.'}
              </span>
            </h2>
          </div>
          <p className="text-titanium text-[15px] leading-relaxed max-w-xl">
            {isVietnamese
              ? 'Chúng tôi nêu rõ các điều khoản về lưu kho, đóng gói, chứng nhận và thanh toán để người mua có thể nhanh chóng đi từ khâu xem xét đến giao hàng.'
              : isIndonesian
                ? 'Kami menjelaskan dengan jelas syarat penyimpanan, pengemasan, sertifikasi, dan pembayaran agar pembeli dapat memproses pesanan dengan cepat, mulai dari peninjauan dokumen hingga pengiriman barang.'
                : isChinese
                  ? '我们明确说明储存、包装、认证和付款条款，帮助买方快速从审核走向发货。'
                  : 'We keep storage, packaging, certification, and payment terms explicit so buyers can move quickly from review to release.'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6">
          <div className="lc-reveal reveal reveal-delay-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            {logistics.map((item) => (
              <div key={item.label} className="rounded-2xl border border-divider bg-surface p-5 shadow-card">
                <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-blue mb-3">
                  {item.label}
                </div>
                <p className="text-[14px] leading-relaxed text-titanium">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="lc-reveal reveal reveal-delay-3 rounded-2xl bg-surface-alt border border-divider p-6 shadow-card">
            <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-blue mb-3">
              {isVietnamese ? 'Cam kết PT. WWI' : isIndonesian ? 'Komitmen PT. WWI' : isChinese ? 'PT. WWI 承诺' : 'PT. WWI Commitment'}
            </div>
            <p className="text-[16px] leading-relaxed text-charcoal">
              {isVietnamese
                ? 'Từ nguồn cung bền vững đến chế biến chính xác, từng giọt dầu đều thể hiện cam kết của chúng tôi với dinh dưỡng vật nuôi vượt trội.'
                : isIndonesian
                  ? 'Dari sumber berkelanjutan hingga proses produksi yang presisi, setiap tetes minyak kami mencerminkan komitmen terhadap kualitas nutrisi ternak yang unggul.'
                  : isChinese
                    ? '从可持续来源到精准加工，每一滴油都体现了我们对卓越动物营养的承诺。'
                    : 'From sustainable sourcing to precision processing, every drop of our oil reflects our commitment to superior animal nutrition.'}
            </p>
            <p className="mt-4 text-[14px] leading-relaxed text-titanium">
              {isVietnamese
                ? 'Nâng cấp chiến lược thức ăn của bạn với lipid biển được thiết kế cho hiệu suất, truy xuất nguồn gốc và sẵn sàng xuất khẩu.'
                : isIndonesian
                  ? 'Optimalkan kualitas pakan Anda dengan minyak ikan (marine lipids) yang mengutamakan performa, aspek ketertelusuran (traceability), dan standar kualitas ekspor.'
                : isChinese
                    ? '使用面向性能、可追溯性与出口标准设计的 marine lipids，提升您的饲料策略。'
                    : 'Elevate your feed strategy with marine lipids crafted for performance, traceability, and export readiness.'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}



